import type { Currency, Product, Purchase, StripeKeyDetail } from "@tellescope/types-models";
import { Label } from "../../label";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { Input } from "../../input";
import { CurrencyInput } from "../../CurrencyInput";
import { Button } from "../../button";
import { type Tab, TabbedContainer } from "../../TabbedContainer";
import { TabsContent } from "../../tabs";
import { MultiSelect } from "../../MultiSelect";
import { getCurrencySymbol } from "@tellescope/utils";

const MOCK_STRIPE_ACCOUNTS: StripeKeyDetail[] = [
  {
    title: "Default",
    key: "1",
  },
  {
    title: "Alternate",
    key: "2",
  },
];

interface ChargePurchaseContentProps {
  enduserId: string;
  products: Product[];
  // TODO: fix this type. The type from schema has weird stuff like validators
  onSubmit: (purchase: {
    enduserId: string;
    productIds?: string[];
    priceIds?: string[];
    cost?: Purchase["cost"];
    stripeKey?: string;
    description?: string;
    subscriptionPriceId?: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export function ChargePurchaseContent({ enduserId, products, onSubmit, onCancel }: ChargePurchaseContentProps) {
  const { t } = useTranslation("payments");
  const tabs: Tab[] = [
    { label: t("Products"), value: "Products" },
    { label: t("Custom Price"), value: "Custom Price" },
  ];
  const [formData, setFormData] = useState<{
    stripeAccountKey?: string;
    productIds?: string[];
    customPrice: boolean;
    cost?: Product["cost"];
    description?: string;
  }>({
    customPrice: false,
  });
  const selectedProducts = useMemo(
    () =>
      formData.stripeAccountKey && formData.productIds?.length
        ? products.filter(({ id }) => formData.productIds?.includes(id))
        : [],
    [formData.productIds, formData.stripeAccountKey, products]
  );
  const totalCost = useMemo(() => {
    if (formData.customPrice) {
      return formData.cost;
    }

    return selectedProducts?.reduce<Product["cost"]>(
      (acc, curr) => {
        acc.amount += curr.cost?.amount ?? 0;
        if (curr.cost?.currency && acc.currency !== curr.cost.currency) {
          acc.currency = curr.cost.currency;
        }

        return acc;
      },
      { amount: 0, currency: "USD" }
    );
  }, [formData.customPrice, formData.cost, selectedProducts]);

  const formIsComplete = useMemo(() => {
    if (!formData.stripeAccountKey) {
      return false;
    }

    if (!totalCost) {
      return false;
    }

    if (formData.customPrice) {
      return formData.cost?.amount !== undefined && formData.cost.amount > 0;
    } else {
      return !!formData.productIds?.length;
    }
  }, [formData.stripeAccountKey, totalCost, formData.customPrice, formData.cost?.amount, formData.productIds]);

  return (
    <form
      className="flex flex-col h-full overflow-y-auto p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!totalCost) {
          // TODO: add validation UI state change when we have `useForm` hook
          return;
        }

        onSubmit({
          cost: totalCost,
          enduserId,
          productIds: formData.customPrice ? [] : formData.productIds,
        });
      }}
    >
      <div className="flex-1 grow-1 space-y-2">
        <div className="space-y-2 p-1 w-1/2">
          <Label htmlFor="stripeAccount">{t("Stripe Account")}</Label>
          <Select
            value={formData.stripeAccountKey}
            onValueChange={(key) => {
              setFormData((prev) => ({
                ...prev,
                stripeAccountKey: key,
                productIds: [],
                cost: undefined,
              }));
            }}
            required
          >
            <SelectTrigger id="stripeAccount" className="w-full">
              <SelectValue placeholder={t("Select Stripe Account")} />
            </SelectTrigger>
            <SelectContent>
              {MOCK_STRIPE_ACCOUNTS.map(({ key, title }) => (
                <SelectItem key={key} value={key}>
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!!formData.stripeAccountKey && (
          <>
            <div className="border-t w-full" />
            <TabbedContainer
              tabs={tabs}
              showCard={false}
              onValueChange={(newVal) => {
                setFormData((prev) => ({
                  ...prev,
                  customPrice: newVal === "Custom Price",
                }));
              }}
              tabsClassName="mt-2"
            >
              <div className="flex flex-col gap-4">
                <TabsContent value="Products">
                  <div className="space-y-2 p-1 max-w-full">
                    <Label htmlFor="products">{t("Select Products")}</Label>
                    <div className="flex gap-4 items-center max-w-full">
                      <MultiSelect
                        id="products"
                        options={
                          products.map(({ title, id }) => ({
                            label: title,
                            value: id,
                          })) ?? []
                        }
                        value={formData.productIds ?? []}
                        onValueChange={(productIds) => {
                          setFormData((prev) => ({
                            ...prev,
                            productIds,
                          }));
                        }}
                        placeholder={t("Select Products")}
                        className="grow"
                        style={{
                          maxWidth: "70%",
                        }}
                      />

                      <div className="flex gap-1 grow-0">
                        <p className="font-bold text-foreground">{t("Total Cost:")}</p>
                        <p className="text-muted-foreground">
                          {`${getCurrencySymbol(totalCost?.currency ?? "USD")}${totalCost?.amount.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="Custom Price">
                  <div className="flex gap-2">
                    <div className="space-y-2 p-1 grow">
                      <Label htmlFor="customCost">{t("Cost")}</Label>
                      <CurrencyInput
                        id="customCost"
                        placeholder={t("Cost")}
                        value={formData.cost?.amount ?? 0}
                        onChange={(amount) => {
                          setFormData((prev) => ({
                            ...prev,
                            cost: {
                              amount,
                              currency: prev.cost?.currency ?? "USD",
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="space-y-2 p-1 w-1/2">
                      <Label htmlFor="currency">{t("Currency")}</Label>
                      <Select
                        value={formData.cost?.currency ?? "USD"}
                        onValueChange={(key) => {
                          setFormData((prev) => ({
                            ...prev,
                            cost: {
                              amount: prev.cost?.amount ?? 0.0,
                              currency: key as Currency,
                            },
                          }));
                        }}
                        required
                      >
                        <SelectTrigger id="stripeAccount" className="w-full">
                          <SelectValue placeholder={t("Select Stripe Account")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">{t("USD")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
                <div className="space-y-2 p-1 max-w-full">
                  <Label htmlFor="purchaseDescription">{t("Purchase Description")}</Label>
                  <Input
                    id="purchaseDescription"
                    placeholder={t("Enter a Purchase Description")}
                    value={formData.description}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                  />
                </div>
              </div>
            </TabbedContainer>
          </>
        )}
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!formIsComplete} type="submit">
          {t("Charge Card on File")}
        </Button>
      </div>
    </form>
  );
}
